// src/components/UserProfile.jsx

import React, { useState } from "react";
import { motion } from "framer-motion";

const UserProfile = ({ userProfile, updateUserProfile, theme, onClose }) => {
  const [profile, setProfile] = useState(userProfile || {
    name: "",
    incomeBracket: "",
    monthlyBudget: "",
    spendingCategories: {
      essentials: 50,
      lifestyle: 30,
      savings: 20
    },
    financialGoal: ""
  });
  
  // Income bracket options
  const incomeBrackets = [
    { value: "under-25k", label: "Under $25,000" },
    { value: "25k-50k", label: "$25,000 - $50,000" },
    { value: "50k-75k", label: "$50,000 - $75,000" },
    { value: "75k-100k", label: "$75,000 - $100,000" },
    { value: "over-100k", label: "Over $100,000" }
  ];
  
  // Financial goals
  const financialGoals = [
    { value: "save-more", label: "Save more money" },
    { value: "reduce-debt", label: "Reduce debt" },
    { value: "big-purchase", label: "Save for a big purchase" },
    { value: "invest", label: "Start investing" },
    { value: "control-spending", label: "Control impulse spending" }
  ];
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile({ ...profile, [name]: value });
  };
  
  // Handle category distribution changes
  const handleCategoryChange = (category, value) => {
    const newValue = Math.max(0, Math.min(100, parseInt(value) || 0));
    
    setProfile({
      ...profile,
      spendingCategories: {
        ...profile.spendingCategories,
        [category]: newValue
      }
    });
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    updateUserProfile(profile);
    onClose();
  };
  
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };
  
  // Removed unused accentStyles variable
  
  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-3xl shadow-xl p-6 w-full max-w-md overflow-y-auto max-h-[90vh]"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Your Profile</h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm text-gray-600 mb-1">Your Name (Optional)</label>
            <input
              type="text"
              name="name"
              value={profile.name}
              onChange={handleChange}
              placeholder="How should we call you?"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2"
            />
          </div>
          
          {/* Income Bracket */}
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Income Bracket (Helps calibrate guilt calculation)
            </label>
            <select
              name="incomeBracket"
              value={profile.incomeBracket}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2"
            >
              <option value="">Prefer not to say</option>
              {incomeBrackets.map((bracket) => (
                <option key={bracket.value} value={bracket.value}>
                  {bracket.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              This helps personalize your guilt scores. We never store or share this information.
            </p>
          </div>
          
          {/* Monthly Discretionary Budget */}
          <div>
            <label className="block text-sm text-gray-600 mb-1">Monthly Spending Budget</label>
            <div className="relative">
              <span className="absolute left-4 top-3">$</span>
              <input
                type="number"
                name="monthlyBudget"
                value={profile.monthlyBudget}
                onChange={handleChange}
                placeholder="0"
                className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Your monthly budget for non-essential spending
            </p>
          </div>
          
          {/* Budget Distribution */}
          <div>
            <label className="block text-sm text-gray-600 mb-2">Your Ideal Budget Breakdown</label>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Essentials (bills, groceries)</span>
                  <span>{profile.spendingCategories.essentials}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={profile.spendingCategories.essentials}
                  onChange={(e) => handleCategoryChange("essentials", e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Lifestyle & Fun</span>
                  <span>{profile.spendingCategories.lifestyle}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={profile.spendingCategories.lifestyle}
                  onChange={(e) => handleCategoryChange("lifestyle", e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Savings & Investments</span>
                  <span>{profile.spendingCategories.savings}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={profile.spendingCategories.savings}
                  onChange={(e) => handleCategoryChange("savings", e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
          </div>
          
          {/* Financial Goal */}
          <div>
            <label className="block text-sm text-gray-600 mb-1">Your Main Financial Goal</label>
            <select
              name="financialGoal"
              value={profile.financialGoal}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2"
            >
              <option value="">Select a goal</option>
              {financialGoals.map((goal) => (
                <option key={goal.value} value={goal.value}>
                  {goal.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Privacy notice */}
          <div className="bg-gray-50 p-3 rounded-xl text-xs text-gray-600">
            <p className="font-semibold mb-1">Privacy Notice</p>
            <p>
              Your financial data is stored locally on your device only and never sent to any server.
              This information is used solely to provide you with personalized insights.
            </p>
          </div>
          
          {/* Submit Button */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <motion.button
              type="button"
              onClick={onClose}
              className="py-3 rounded-xl border border-gray-300 font-semibold"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </motion.button>
            
            <motion.button
              type="submit"
              className={`py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Save Profile
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default UserProfile;
